import React, { useState } from 'react';
import { BookOpen, Clock, AlertCircle, Loader2 } from 'lucide-react';
import { format, parseISO, formatDistanceToNowStrict } from 'date-fns';
import { usePhilosophy, usePhilosophyVersions } from '../hooks/usePhilosophy';

/**
 * Investment-philosophy viewer. Two panels side-by-side:
 *
 *   - Versions picker (left): metadata list of every v{N} doc. Clicking a
 *     row loads that version into the right-hand panel. The current /
 *     selected row gets the .btn-primary treatment.
 *   - Content panel (right): full prose for whichever version is
 *     selected. Rendered with `white-space: pre-wrap` so paragraph breaks
 *     and indentation are preserved without pulling in a markdown library.
 *
 * On a fresh app with no philosophy yet, both endpoints 404 and we show
 * an empty state pointing at the trigger options.
 */
const Philosophy = () => {
  // null = "show the current alias"; a number selects a specific historical version.
  const [selectedVersion, setSelectedVersion] = useState(null);

  const { philosophy, loading: contentLoading, error: contentError } = usePhilosophy(selectedVersion);
  const { versions, loading: versionsLoading, error: versionsError } = usePhilosophyVersions();

  const noPhilosophyYet =
    !contentLoading && !versionsLoading && !philosophy && versions.length === 0;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Investment Philosophy</h1>
          <p>
            Russ's synthesized investing philosophy, regenerated from messages, transcripts,
            and prior reports. Updates automatically every other Sunday; can also be triggered
            manually or alongside a transcript upload.
          </p>
        </div>
      </div>

      {noPhilosophyYet ? (
        <EmptyState />
      ) : (
        <div className="philosophy-layout">
          {/* Versions picker (left) */}
          <aside className="philosophy-versions card" aria-label="Philosophy versions">
            <div style={{ marginBottom: '0.75rem' }}>
              <h2 style={{
                fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)',
                textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0,
              }}>
                Versions
              </h2>
            </div>

            {versionsLoading ? (
              <Spinner />
            ) : versionsError ? (
              <ErrorBlock message={versionsError} />
            ) : versions.length === 0 ? (
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: 0 }}>
                No prior versions yet.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                <VersionButton
                  active={selectedVersion === null}
                  onClick={() => setSelectedVersion(null)}
                  label="Current"
                  sublabel={
                    versions[0]
                      ? `v${versions[0].version} · ${triggerLabel(versions[0].triggeredBy)}`
                      : null
                  }
                />
                {versions.map((v) => (
                  <VersionButton
                    key={v.version}
                    active={selectedVersion === v.version}
                    onClick={() => setSelectedVersion(v.version)}
                    label={`v${v.version}`}
                    sublabel={`${formatDate(v.updatedAt)} · ${triggerLabel(v.triggeredBy)}`}
                  />
                ))}
              </div>
            )}
          </aside>

          {/* Content panel (right) */}
          <section className="philosophy-content card">
            {contentLoading ? (
              <Spinner />
            ) : contentError && !philosophy ? (
              <ErrorBlock message={contentError} />
            ) : !philosophy ? (
              <p style={{ color: 'var(--text-secondary)' }}>No philosophy loaded.</p>
            ) : (
              <>
                <div className="philosophy-meta">
                  <BookOpen size={20} style={{ color: 'var(--primary-color)' }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600 }}>
                      {selectedVersion === null ? 'Current philosophy' : `Philosophy v${philosophy.version}`}
                    </h2>
                    <div style={{
                      display: 'flex', flexWrap: 'wrap', gap: '0.75rem',
                      marginTop: '0.25rem', fontSize: '0.8125rem', color: 'var(--text-secondary)',
                    }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                        <Clock size={14} />
                        {formatDate(philosophy.updatedAt)}
                        {' '}({formatDistanceToNowStrict(parseISO(philosophy.updatedAt), { addSuffix: true })})
                      </span>
                      <span>v{philosophy.version}</span>
                      {philosophy.triggeredBy && (
                        <span>via {triggerLabel(philosophy.triggeredBy)}</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="philosophy-body">
                  {philosophy.content || <em style={{ color: 'var(--text-secondary)' }}>No content.</em>}
                </div>
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
};

// --- Subcomponents --------------------------------------------------

const VersionButton = ({ active, onClick, label, sublabel }) => (
  <button
    type="button"
    onClick={onClick}
    className={`btn ${active ? 'btn-primary' : 'btn-secondary'}`}
    style={{
      display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
      textAlign: 'left', padding: '0.5rem 0.75rem', gap: '0.125rem',
    }}
  >
    <strong style={{ fontSize: '0.875rem' }}>{label}</strong>
    {sublabel && (
      <span style={{ fontSize: '0.75rem', opacity: 0.85 }}>{sublabel}</span>
    )}
  </button>
);

const EmptyState = () => (
  <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
    <BookOpen size={48} style={{ color: 'var(--text-secondary)', marginBottom: '0.75rem' }} />
    <h2 style={{ marginTop: 0 }}>No philosophy yet</h2>
    <p style={{ color: 'var(--text-secondary)', maxWidth: '40rem', margin: '0 auto' }}>
      The first version will be generated by the next biweekly timer (Sunday 06:00 UTC),
      automatically when you upload a transcript with <code>triggerPhilosophyUpdate: true</code>,
      or on demand by hitting <code>POST /api/philosophy/update</code> with a function key.
    </p>
  </div>
);

const Spinner = () => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-secondary)' }}>
    <Loader2 size={16} className="spin" />
    Loading...
  </div>
);

const ErrorBlock = ({ message }) => (
  <div className="error" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
    <AlertCircle size={20} />
    <span>{message}</span>
  </div>
);

// --- Helpers --------------------------------------------------------

const formatDate = (raw) => {
  if (!raw) return '';
  try { return format(parseISO(raw), 'MMM dd, yyyy'); }
  catch { return raw; }
};

/**
 * Renders the `triggeredBy` audit string in a human-friendly form. Values:
 *   "manual"             -> Manual
 *   "timer-biweekly"     -> Biweekly timer
 *   "transcript:{id}"    -> Transcript upload
 *   anything else         -> the raw string
 */
const triggerLabel = (raw) => {
  if (!raw) return 'unknown';
  if (raw === 'manual')          return 'Manual';
  if (raw === 'timer-biweekly')  return 'Biweekly timer';
  if (raw.startsWith('transcript:')) return 'Transcript upload';
  return raw;
};

export default Philosophy;
